import styled, { css, keyframes } from "styled-components";

const alertPulse = keyframes`
  0%, 100% {
    border-color: rgba(245, 173, 24, 0.42);
    box-shadow: inset 4px 0 0 #f5ad18, 0 0 0 rgba(245, 173, 24, 0);
  }
  50% {
    border-color: rgba(245, 173, 24, 0.82);
    box-shadow: inset 4px 0 0 #f5ad18, 0 0 15px rgba(245, 173, 24, 0.12);
  }
`;

const cardPulse = keyframes`
  0%, 100% {
    opacity: 0.22;
    box-shadow: 0 0 0 rgba(255, 66, 91, 0);
  }
  50% {
    opacity: 0.95;
    box-shadow: 0 0 16px rgba(255, 66, 91, 0.36);
  }
`;

export const dangerFillPulse = keyframes`
  0%, 100% {
    box-shadow:
      0 6px 16px rgba(0, 0, 0, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.035),
      inset 0 0 0 999px rgba(255, 66, 91, 0.06);
  }
  50% {
    box-shadow:
      0 0 24px rgba(255, 66, 91, 0.44),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 0 0 999px rgba(255, 66, 91, 0.48);
  }
`;

export const warningFillPulse = keyframes`
  0%, 100% {
    box-shadow:
      0 6px 16px rgba(0, 0, 0, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.035),
      inset 0 0 0 999px rgba(245, 173, 24, 0.05);
  }
  50% {
    box-shadow:
      0 0 22px rgba(245, 173, 24, 0.38),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 0 0 999px rgba(245, 173, 24, 0.42);
  }
`;

const toneColor = {
  healthy: "#35d46f",
  warning: "#f5ad18",
  danger: "#ff425b",
  offline: "#778390",
};

export const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  color: #f6f8fb;
  background:
    radial-gradient(ellipse at 48% 0%, #082231 0%, transparent 58%),
    #030c13;
  ${({ $cloud }) => $cloud && css`
    height: 100dvh; min-height: 0; display: flex; flex-direction: column;
    > header { flex-shrink: 0; }
  `}
`;

export const Header = styled.header`
  min-height: 48px;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: rgba(2, 14, 23, 0.96);
  border-bottom: 1px solid #173240;
  backdrop-filter: blur(14px);
  position: sticky;
  top: 0;
  z-index: 50;

  @media (max-width: 680px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
`;

export const LogoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  img {
    width: 29px;
    height: 29px;
    object-fit: contain;
  }

  > span:first-of-type {
    font-size: 17px;
    font-weight: 680;
    letter-spacing: -0.01em;
  }
`;

export const EnvironmentBadge = styled.span`
  padding: 3px 9px;
  color: #77c9ff;
  background: rgba(54, 152, 255, 0.13);
  border: 1px solid rgba(54, 152, 255, 0.55);
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
`;

export const HeaderSummary = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  color: #9aa7b4;
  font-size: 11px;

  strong {
    color: #f6f8fb;
    font-weight: 700;
  }
`;

export const SummaryChip = styled.div`
  min-height: 31px;
  padding: 4px 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #233843;
  border-radius: 6px;
  background: linear-gradient(135deg, #071d28, #04131c);
  white-space: nowrap;

  > svg { width: 16px; height: 16px; color: ${({ $color }) => $color}; }
  strong { font-size: 13px; }
  @media (max-width: 1000px) { padding: 4px 7px; > span:last-child:not(:first-child) { display: none; } }
`;

export const StatusDot = styled.span`
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color = "#35d46f" }) => $color};
  box-shadow: 0 0 6px color-mix(in srgb, ${({ $color = "#35d46f" }) => $color} 35%, transparent);
`;

export const AlertBar = styled.div`
  width: 100%;
  min-height: 34px;
  padding: 6px 13px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  color: #e8edf1;
  background: linear-gradient(100deg, rgba(49, 33, 9, 0.82), rgba(13, 17, 20, 0.96) 44%);
  border: 1px solid rgba(245, 173, 24, 0.55);
  border-radius: 7px;
  font-size: 11px;
  font-weight: 500;
  animation: ${alertPulse} 1.4s ease-in-out infinite;
  svg { flex-shrink: 0; color: #f5ad18; }
  strong {
    color: #fff2cf;
    font-size: 11px;
    font-weight: 750;
    white-space: nowrap;
  }
  span {
    min-width: 0;
    padding-left: 10px;
    color: #c6cdd3;
    border-left: 1px solid rgba(245, 173, 24, 0.38);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-wrap: wrap;
    span { width: 100%; padding: 4px 0 0 26px; border-left: 0; }
  }
`;

export const Content = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 8px;
  display: grid;
  grid-template-columns: ${({ $collapsed }) => $collapsed ? "44px" : "196px"} minmax(0, 1fr);
  gap: 8px;
  ${({ $cloud }) => $cloud && css`
    flex: 1; min-height: 0; grid-template-rows: minmax(0, 1fr);
  `}

  @media (max-width: 860px) {
    grid-template-columns: ${({ $cloud }) => $cloud ? "44px minmax(0, 1fr)" : "1fr"};
  }
`;

export const DashboardArea = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Main = styled.main`
  min-width: 0;
  min-height: 0;
  height: ${({ $height = 560 }) => `${$height}px`};
  display: grid;
  grid-template-columns: repeat(
    ${({ $columns = 1 }) => $columns},
    minmax(0, 1fr)
  );
  grid-template-rows: repeat(
    ${({ $rows = 1 }) => $rows},
    minmax(0, ${({ $cardHeight = 0 }) => `${$cardHeight}px`})
  );
  gap: 8px;
  align-items: stretch;
  align-content: start;
`;

export const Sidebar = styled.aside`
  min-width: 0;
  height: calc(100dvh - 64px);
  padding: ${({ $collapsed }) => $collapsed ? "8px 3px" : "8px 6px 6px"};
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 56px;
  z-index: 40;
  align-self: start;
  overflow: visible;
  background: linear-gradient(155deg, #071d28, #04131d);
  border: 1px solid #24414e;
  border-radius: 8px;
  ${({ $cloud }) => $cloud && css`height: 100%; position: relative; top: auto;`}

  @media (max-width: 860px) {
    height: auto;
    max-height: 280px;
    position: static;
    overflow-y: auto;
    ${({ $cloud }) => $cloud && css`
      height: 100%; max-height: none; padding: 6px 3px;
      overflow: visible; position: relative; top: auto;
      > nav button { justify-content: center; padding: 6px 3px; }
      > nav span, > nav b, > nav button:last-child > svg:last-child { display: none; }
      > button { display: none; }
      > [data-rotation-settings] { position: absolute; left: 47px; top: 75px; width: 190px; z-index: 60; }
    `}
  }
`;

export const SidebarHeader = styled.div`
  min-height: 24px;
  padding: 0 2px 5px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
`;

export const SidebarTitle = styled.h2`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #f1f5f8;
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.01em;

  svg {
    flex: 0 0 auto;
    color: #35d46f;
  }
`;

export const SidebarCount = styled.span`
  min-width: 23px;
  height: 20px;
  padding: 0 6px;
  display: inline-grid;
  place-items: center;
  color: #cbd4dc;
  background: #202b34;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
`;

export const ServerList = styled.div`
  min-height: 0;
  padding-top: 7px;
  flex: 1 1 auto;
  display: grid;
  grid-template-rows: repeat(
    ${({ $items = 1 }) => Math.max(1, $items)},
    minmax(30px, 37px)
  );
  gap: 1px;
  align-content: start;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #31505e transparent;

  @media (max-width: 860px) {
    padding: 10px 0 2px;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 190px), 1fr));
    grid-template-rows: none;
    grid-auto-rows: minmax(32px, auto);
  }
`;

export const SearchField = styled.label`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  height: 28px;
  padding: 0 7px;
  margin: 3px 0 7px;
  color: #9aaaae;
  background: #03131e;
  border: 1px solid #29424d;
  border-radius: 4px;
  input { min-width: 0; width: 100%; outline: none; border: 0; background: transparent; color: #f3f7fa; font-size: 10px; }
  &:focus-within { border-color: #3698ff; }
`;

export const FilterLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 5px;
  color: #b6c5ce;
  font-size: 10px;
`;

export const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 3px;
  flex-shrink: 0;
`;

export const FilterButton = styled.button`
  min-width: 0;
  height: 22px;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  color: ${({ $selected, $color }) => $selected ? "#fff" : $color};
  background: ${({ $selected }) => $selected ? "#123246" : "#041620"};
  border: 1px solid ${({ $selected, $color }) => $selected ? $color : "#233d48"};
  border-radius: 4px;
  font-size: 9px;
  cursor: pointer;
  strong { color: #d3dfe6; font-size: 9px; font-weight: 500; }
  &:hover, &:focus-visible { border-color: ${({ $color }) => $color}; }
`;

export const SidebarCollapse = styled.button`
  min-height: 28px;
  flex-shrink: 0;
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #becad2;
  border: 1px solid #203844;
  border-radius: 4px;
  background: #041721;
  font-size: 10px;
  cursor: pointer;
  &:hover { color: #fff; background: #102c3b; }
`;

export const ServerListItem = styled.div`
  min-width: 0;
  min-height: 0;
  height: 100%;
  max-height: 38px;
  padding: 0 2px 0 7px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 4px;
  background: ${({ $problematic, $active }) =>
    $problematic
      ? "rgba(255, 66, 91, 0.1)"
      : $active
        ? "transparent"
        : "rgba(119, 131, 144, 0.08)"};
  border: 1px solid ${({ $problematic, $active }) =>
    $problematic
      ? "rgba(255, 66, 91, 0.28)"
      : $active
        ? "transparent"
        : "rgba(119, 131, 144, 0.2)"};
  border-radius: 4px;
  transition: background 160ms ease, border-color 160ms ease;

  &:hover {
    background: #18222b;
    border-color: #2b3944;
  }

  @media (max-width: 860px) {
    min-width: 0;
  }
`;

export const SidebarServerButton = styled.button`
  min-width: 0;
  height: 100%;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  color: ${({ $active, $online }) =>
    !$active ? "#8d99a5" : $online ? "#dfe7ed" : "#ff7184"};
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;

  &:hover strong,
  &:focus-visible strong {
    color: ${({ $active }) => ($active ? "#55db87" : "#c0c8cf")};
  }

  &:focus-visible {
    outline: 2px solid #55db87;
    outline-offset: 2px;
    border-radius: 6px;
  }
`;

export const SidebarServerIdentity = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong,
  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1;
  }

  strong {
    color: inherit;
    font-size: 0.68rem;
    font-weight: 750;
    transition: color 160ms ease;
  }
`;

export const SidebarServerState = styled.span`
  color: ${({ $active, $online }) =>
    !$active ? "#8d99a5" : $online ? "#55db87" : "#ff7184"};
  font-size: 0.52rem;
  font-weight: 700;
  letter-spacing: 0.025em;
`;

export const ActivationToggle = styled.button`
  --toggle-color: ${({ $active }) => ($active ? "#35d46f" : "#778390")};
  width: 27px;
  height: 16px;
  padding: 2px;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  background: color-mix(in srgb, var(--toggle-color) 25%, #111920);
  border: 1px solid color-mix(in srgb, var(--toggle-color) 72%, transparent);
  border-radius: 999px;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease;

  span {
    width: 10px;
    height: 10px;
    display: block;
    background: var(--toggle-color);
    border-radius: 50%;
    box-shadow: 0 0 7px color-mix(in srgb, var(--toggle-color) 65%, transparent);
    transform: translateX(${({ $active }) => ($active ? "11px" : "0")});
    transition: transform 180ms ease, background 160ms ease;
  }

  &:hover {
    background: color-mix(in srgb, var(--toggle-color) 36%, #111920);
  }

  &:focus-visible {
    outline: 2px solid var(--toggle-color);
    outline-offset: 2px;
  }
`;

export const ServerCardViewport = styled.div`
  min-width: 0;
  min-height: 0;
  position: relative;
  overflow: visible;
`;

export const ServerCard = styled.article`
  --accent: ${({ $tone = "healthy" }) => toneColor[$tone] || toneColor.healthy};
  width: ${({ $width = 360 }) => `${$width}px`};
  height: ${({ $height = 224 }) => `${$height}px`};
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 5px;
  padding: 7px 8px 3px;
  background: linear-gradient(135deg, #0a1c26, #06141d 58%, #041019);
  border: 1px solid #31505e;
  border-left: 5px solid var(--accent);
  border-radius: 8px;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.035);
  overflow: visible;
  isolation: isolate;
  transform: translate(-50%, -50%) scale(${({ $scale = 1 }) => $scale});
  transform-origin: center;
  transition: border-color 180ms ease;
  cursor: pointer;

  ${({ $tone }) => $tone === "danger" && css`
    animation: ${dangerFillPulse} 1.35s ease-in-out infinite;
  `}

  ${({ $tone }) => $tone === "warning" && css`
    animation: ${warningFillPulse} 1.55s ease-in-out infinite;
  `}

  ${({ $problematic }) => $problematic && css`
    border-color: rgba(255, 66, 91, 0.62);
    border-left-color: #ff425b;

    &::after {
      content: "";
      position: absolute;
      inset: -1px;
      z-index: -1;
      border-radius: inherit;
      border: 1px solid #ff425b;
      background: transparent;
      opacity: 0;
      pointer-events: none;
      animation: ${cardPulse} 1.4s ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
      &::after {
        animation: none;
        opacity: 0.55;
      }
    }
  `}

  &:hover {
    border-color: ${({ $problematic }) =>
      $problematic
        ? "rgba(255, 66, 91, 0.82)"
        : "color-mix(in srgb, #f4f7fa 82%, var(--accent))"};
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

export const IdentityPanel = styled.section`
  min-width: 0;
  padding: 0 1px 2px;
  border-bottom: 1px solid rgba(76, 113, 128, 0.22);
`;

export const IdentityHeader = styled.div`
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) auto;
  gap: 6px;
  align-items: center;

  > div:nth-child(2) {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 7px;
  }
`;

export const ServerIcon = styled.div`
  width: 16px;
  height: 20px;
  display: grid;
  place-items: center;
  position: relative;
  color: #c3cbd3;

  svg {
    display: none;
  }
`;

export const PresenceDot = styled.span`
  width: 10px;
  height: 10px;
  position: absolute;
  inset: 50% auto auto 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: ${({ $online }) => $online ? "#35d46f" : "#ff425b"};
  box-shadow: 0 0 12px ${({ $online }) => $online ? "rgba(53, 212, 111, .65)" : "rgba(255, 66, 91, .65)"};
`;

export const ServerNameButton = styled.button`
  display: block;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f7f9fb;
  font: inherit;
  min-width: 0;
  font-size: 16px;
  font-weight: 760;
  line-height: 1.15;
  letter-spacing: -0.015em;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: #55db87;
  }

  &:focus-visible {
    outline: 2px solid #55db87;
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

export const StatusBadge = styled.span`
  width: fit-content;
  margin-top: 0;
  padding: 2px 7px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: ${({ $tone }) => toneColor[$tone] || toneColor.healthy};
  background: color-mix(in srgb, ${({ $tone }) => toneColor[$tone] || toneColor.healthy} 8%, #04151d);
  border: 1px solid color-mix(in srgb, ${({ $tone }) => toneColor[$tone] || toneColor.healthy} 30%, transparent);
  border-radius: 8px;
  font-size: 9px;
  font-weight: 700;
`;

export const ActionSlot = styled.div`
  align-self: start;
`;

export const MonitorPanel = styled.section`
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-columns: minmax(0, 0.96fr) minmax(0, 1.04fr);
  grid-template-rows: minmax(0, 1fr) 24px;
  grid-template-areas:
    "metrics charts"
    "status status";
  column-gap: 5px;
  row-gap: 2px;
`;

export const MetricsGrid = styled.div`
  grid-area: metrics;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
`;

export const MetricCard = styled.div`
  min-width: 0;
  min-height: 0;
  padding: 1px 1px 0;
  display: flex;
  flex-direction: column;
`;

export const MetricCardHeader = styled.div`
  min-height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
    color: ${({ $color }) => $color};
    filter: drop-shadow(0 0 5px color-mix(in srgb, ${({ $color }) => $color} 40%, transparent));
  }
`;

export const MetricTitle = styled.div`
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 7px;
  color: #f3f6f9;
  font-size: 10.5px;
  font-weight: 650;
  white-space: nowrap;

  span {
    color: #a1acb7;
    font-size: 0.68rem;
    font-weight: 500;
  }
`;

export const MetricNumber = styled.text`
  fill: #f4f8fb;
  font-size: 32px;
  font-weight: 720;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
  paint-order: stroke;
  stroke: rgba(2, 10, 15, 0.9);
  stroke-width: 2px;
`;

export const GaugeBody = styled.div`
  min-width: 0;
  min-height: 0;
  flex: 1;
  position: relative;
  display: grid;
  place-items: center;
`;

export const GaugeSvg = styled.svg`
  width: 100%;
  height: 62px;
  display: block;
  overflow: visible;
`;

export const GaugeTrack = styled.path`
  fill: none;
  stroke: #1d3e4d;
  stroke-width: 10;
  stroke-linecap: round;
  opacity: 0.95;
`;

export const GaugeArc = styled.path`
  fill: none;
  stroke-width: 10;
  stroke-linecap: round;
  filter: drop-shadow(0 0 4px ${({ $glowColor = "#35d46f" }) => $glowColor});
  transition: stroke 250ms ease, stroke-dasharray 420ms ease;
`;

export const GaugeTick = styled.line`
  stroke: ${({ $major }) => ($major ? "#d4dce3" : "#a1b2bd")};
  stroke-width: ${({ $major }) => ($major ? 2.2 : 1.5)};
  stroke-linecap: round;
  opacity: ${({ $major }) => ($major ? 0.82 : 0.54)};
`;

export const GaugeNeedleOutline = styled.line`
  stroke: #02080d;
  stroke-width: 8;
  stroke-linecap: round;
  transform-box: view-box;
  transform-origin: 95px 86px;
  transform: rotate(${({ $rotation = -90 }) => `${$rotation}deg`});
  opacity: 0.92;
  transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
`;

export const GaugeNeedle = styled.line`
  stroke: #ffffff;
  stroke-width: 4.5;
  stroke-linecap: round;
  transform-box: view-box;
  transform-origin: 95px 86px;
  transform: rotate(${({ $rotation = -90 }) => `${$rotation}deg`});
  filter: drop-shadow(0 0 2px #02080d) drop-shadow(0 0 4px ${({ $color }) => $color});
  transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1), stroke 250ms ease;
`;

export const GaugeHub = styled.circle`
  fill: #e9eef2;
  stroke: ${({ $color }) => $color};
  stroke-width: 3;
  filter: drop-shadow(0 0 5px ${({ $color }) => $color});
`;

export const ChartsContainer = styled.div`
  grid-area: charts;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 2px;
  padding-left: 5px;
  border-left: 1px solid rgba(76, 113, 128, 0.32);

  > * + * {
    border-top: 1px solid rgba(76, 113, 128, 0.22);
  }
`;

export const StatusStrip = styled.div`
  grid-area: status;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  overflow: hidden;
  border-top: 1px solid #2b4855;
  background: rgba(2, 13, 20, 0.22);
`;

export const StatusItem = styled.div`
  min-width: 0;
  padding: 1px 4px 0;
  display: flex;
  align-items: center;
  gap: 3px;
  border-right: 1px solid rgba(76, 113, 128, 0.22);

  &:last-child {
    border-right: 0;
  }

  svg {
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
    color: ${({ $color = "#8d99a5" }) => $color};
  }

`;

export const StatusText = styled.div`
  min-width: 0;

  span,
  strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    color: #a4aeba;
    font-size: 7px;
    line-height: 1.05;
  }

  strong {
    margin-top: 0;
    color: ${({ $color = "#f5f7f9" }) => $color};
    font-size: 9px;
    font-weight: 500;
    line-height: 1.05;
  }
`;

export const EmptyState = styled.div`
  grid-column: 1 / -1;
  min-height: 180px;
  padding: 24px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #9eabb7;
  background: rgba(17, 24, 31, 0.72);
  border: 1px dashed #33414d;
  border-radius: 18px;

  svg {
    margin: 0 auto 14px;
    color: #546371;
  }

  strong {
    display: block;
    margin-bottom: 6px;
    color: #f3f6f9;
    font-size: 1.1rem;
  }
`;

export const CardModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  padding: 24px;
  display: grid;
  place-items: center;
  background: rgba(0, 7, 12, 0.84);
  backdrop-filter: blur(10px);
`;

export const CardModalDialog = styled.div`
  width: min(1120px, calc(100vw - 48px));
  position: relative;
`;

export const CardModalClose = styled.button`
  width: 38px;
  height: 38px;
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  padding: 0;
  display: grid;
  place-items: center;
  color: #eaf2f6;
  background: rgba(4, 18, 27, 0.94);
  border: 1px solid #42606d;
  border-radius: 50%;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: #fff;
    border-color: #7da5b5;
    background: #102b38;
  }

  &:focus-visible {
    outline: 2px solid #3698ff;
    outline-offset: 2px;
  }
`;

export const ExpandedServerCard = styled(ServerCard)`
  width: 100%;
  height: min(620px, calc(100dvh - 72px));
  min-height: 480px;
  position: relative;
  inset: auto;
  transform: none;
  padding: 18px 22px 16px;
  gap: 12px;
  cursor: default;
  overflow: hidden;
  border-left-width: 8px;
  border-radius: 14px;

  ${IdentityPanel} {
    padding-bottom: 10px;
  }

  ${IdentityHeader} {
    grid-template-columns: 24px minmax(0, 1fr) auto;
    gap: 12px;
  }

  ${ServerIcon} {
    width: 24px;
    height: 30px;
  }

  ${PresenceDot} {
    width: 16px;
    height: 16px;
  }

  ${ServerNameButton} {
    font-size: 28px;
  }

  ${StatusBadge} {
    padding: 4px 10px;
    font-size: 13px;
    border-radius: 999px;
  }

  ${MonitorPanel} {
    grid-template-columns: minmax(0, 0.96fr) minmax(0, 1.04fr);
    grid-template-rows: minmax(0, 1fr) 56px;
    column-gap: 18px;
    row-gap: 8px;
  }

  ${MetricsGrid} {
    gap: 12px;
  }

  ${MetricCardHeader} {
    min-height: 28px;
  }

  ${MetricTitle} {
    font-size: 18px;
  }

  ${GaugeSvg} {
    height: min(240px, 30dvh);
  }

  ${MetricNumber} {
    font-size: 29px;
    stroke-width: 1.4px;
  }

  ${ChartsContainer} {
    gap: 10px;
    padding-left: 18px;
  }

  ${StatusStrip} {
    border-radius: 8px;
  }

  ${StatusItem} {
    padding: 7px 12px;
    gap: 8px;
    align-items: center;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  ${StatusText} {
    span {
      font-size: 11px;
    }

    strong {
      font-size: 15px;
    }
  }

  @media (max-width: 760px) {
    height: calc(100dvh - 28px);
    min-height: 0;
    padding: 14px;

    ${MonitorPanel} {
      grid-template-columns: 1fr;
      grid-template-rows: minmax(180px, 0.9fr) minmax(220px, 1.1fr) auto;
      grid-template-areas:
        "metrics"
        "charts"
        "status";
      overflow-y: auto;
    }

    ${ChartsContainer} {
      padding-left: 0;
      border-left: 0;
    }

    ${StatusStrip} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
`;
